/**
 * OnbEmail — onboarding step 2 (email + password signup).
 *
 * Two `Field` inputs, a privacy note, and a "Continue" CTA that calls
 * `useEmailSignup` (`POST /auth/signup`). The chosen UI language (ui-store) is
 * sent as the account language. On success the flow advances to the OTP step
 * with the new `user_id` and — in non-prod — the echoed `dev_otp` (shown
 * on-screen since QA has no real email delivery).
 *
 * Validation: client-side via the shared `signupSchema` (email shape + 12-char
 * password minimum), then server errors (409 email-exists / 422 breached /
 * 422 too-short) map back onto the offending field. Colour is never the only
 * error signal — the helper text states the reason.
 *
 * 2026-08: every field now says WHY it is being asked, right where it is asked.
 * The audience is a first-time renovator who is already worried about being
 * taken advantage of; "we use your email only to sign you in and for important
 * notices" belongs next to the field, not in a privacy policy. The step also
 * gained the "I already have an account" door — the single most common reason a
 * signup form fails is that the person standing in front of it is not new.
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button, Field, Icon } from "@garden/ui";

import { ONBOARDING_NS, useEmailSignup } from "@/features/onboarding/api";
import { STEP_H1_CLASS } from "@/features/onboarding/steps/shared";
import { signupSchema } from "@/features/auth/schemas";
import { ApiError } from "@/lib/api-client";
import { useUiStore } from "@/lib/ui-store";

/** Email + password only — language comes from the ui-store, not a field. */
const credentialsSchema = signupSchema.omit({ language: true });

type FieldName = "email" | "password";

/** Map a signup server error to (field, onboarding i18n key). */
function serverErrorToField(error: unknown): {
  field: FieldName | "root";
  key: string;
} {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "auth.signup.email_exists":
        return { field: "email", key: "email.errExists" };
      case "password.breached":
        return { field: "password", key: "email.errBreached" };
      case "password.too_short":
        return { field: "password", key: "email.errShort" };
    }
  }
  return { field: "root", key: "email.errGeneric" };
}

export interface OnbEmailProps {
  /**
   * Called once the account is created + the OTP sent: the email, the new
   * `user_id` (verify payload), the raw password (kept by the flow so a
   * "Resend" can re-issue), and — non-prod only — the echoed `dev_otp`.
   */
  onSent: (args: {
    email: string;
    password: string;
    userId: string;
    devOtp: string | null;
  }) => void;
  /** The escape hatch for a returning user (→ /login). */
  onSignIn: () => void;
}

export function OnbEmail({ onSent, onSignIn }: OnbEmailProps): ReactElement {
  const { t } = useTranslation(ONBOARDING_NS);
  const language = useUiStore((s) => s.language);
  const signupMutation = useEmailSignup();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorField, setErrorField] = useState<FieldName | "root" | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const clearError = (): void => {
    if (errorKey !== null) {
      setErrorKey(null);
      setErrorField(null);
    }
  };

  const submit = (): void => {
    const parsed = credentialsSchema.safeParse({ email, password });
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      const field: FieldName = first?.path[0] === "password" ? "password" : "email";
      setErrorField(field);
      setErrorKey(field === "password" ? "email.errShort" : "email.errInvalid");
      return;
    }
    setErrorKey(null);
    setErrorField(null);
    signupMutation.mutate(
      { email: parsed.data.email, password: parsed.data.password, language },
      {
        onSuccess: (data) =>
          onSent({
            email: data.email,
            password: parsed.data.password,
            userId: data.user_id,
            devOtp: data.dev_otp ?? null,
          }),
        onError: (error) => {
          const mapped = serverErrorToField(error);
          setErrorField(mapped.field);
          setErrorKey(mapped.key);
        },
      },
    );
  };

  const emailError = errorField === "email" && errorKey ? t(errorKey) : undefined;
  const passwordError =
    errorField === "password" && errorKey ? t(errorKey) : undefined;
  const rootError = errorField === "root" && errorKey ? t(errorKey) : undefined;

  return (
    <div className="flex flex-1 flex-col" data-testid="onb-email">
      <div className="flex-1">
        <h1 className={STEP_H1_CLASS}>{t("email.h1")}</h1>
        <p className="mb-6 mt-2.5 text-[15px] leading-[1.5] text-[var(--g-ink-2)]">
          {t("email.lede")}
        </p>

        {/*
          The "why" lines are rendered HERE rather than passed to `Field`'s
          `hint`: `Field` styles its helper at `--g-ink-3` (38% ink), which
          measures ~3.4:1 on the cream ground and fails WCAG 1.4.3 — axe flags
          it. `--g-ink-2` clears 4.5:1. The `aria-describedby` wiring `hint`
          would have given us is reproduced explicitly so the reason is still
          announced with the field. When a field is in error, `Field` renders
          the error in its own helper slot and the why-line stands down, so the
          user never gets two competing lines under one input.
        */}
        <div className="flex flex-col gap-4">
          <div>
            <Field
              label={t("email.emailLabel")}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={t("email.emailPlaceholder")}
              value={email}
              error={emailError}
              aria-describedby="onb-email-why"
              data-testid="onb-email-input"
              onChange={(e) => {
                setEmail(e.target.value);
                clearError();
              }}
            />
            {emailError === undefined ? (
              <p
                id="onb-email-why"
                className="mt-1.5 text-[12.5px] leading-snug text-[var(--g-ink-2)]"
              >
                {t("email.emailWhy")}
              </p>
            ) : null}
          </div>
          <div>
            <Field
              label={t("email.passwordLabel")}
              type="password"
              autoComplete="new-password"
              placeholder={t("email.passwordPlaceholder")}
              value={password}
              error={passwordError}
              aria-describedby="onb-password-why"
              data-testid="onb-password-input"
              onChange={(e) => {
                setPassword(e.target.value);
                clearError();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
              }}
            />
            {passwordError === undefined ? (
              <p
                id="onb-password-why"
                className="mt-1.5 text-[12.5px] leading-snug text-[var(--g-ink-2)]"
              >
                {t("email.passwordHint")}
              </p>
            ) : null}
          </div>
        </div>

        {rootError !== undefined ? (
          <div
            role="alert"
            data-testid="onb-email-error"
            className="mt-4 rounded-[var(--g-radius)] border border-destructive/40 bg-destructive/[0.05] px-3.5 py-3 text-[13.5px] leading-snug text-[var(--g-error)]"
          >
            {rootError}
          </div>
        ) : null}

        {/* The promise, stated where the ask happens. */}
        <div className="mt-[18px] flex gap-2.5 rounded-[var(--g-radius)] border border-[var(--g-rule)] bg-[var(--g-sunken)] px-3.5 py-3 text-[var(--g-ink-2)]">
          <Icon name="shield" size={18} className="mt-px shrink-0" />
          <span className="text-[13px] leading-[1.5]">{t("email.privacy")}</span>
        </div>
      </div>

      <div className="flex flex-col gap-2.5 pt-6">
        <Button
          size="lg"
          full
          trailing="arrowR"
          loading={signupMutation.isPending}
          data-testid="onb-email-continue"
          onClick={submit}
        >
          {t("cta.continue")}
        </Button>
        <Button
          size="lg"
          variant="ghost"
          full
          data-testid="onb-email-signin"
          onClick={onSignIn}
        >
          {t("email.haveAccount")}
        </Button>
      </div>
    </div>
  );
}

export default OnbEmail;
